import { signS3CompanionSessionAttachment } from "@api/SignS3";
import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SEND_COMPANION_SESSION_MESSAGE } from "@graphql/mutations/SendCompanionSessionMessage";
import { UPLOAD_COMPANION_SESSION_ATTACHMENT } from "@graphql/mutations/UploadCompanionSessionAttachment";
import { useStreamResult } from "@hooks";
import {
  AvailableCompanions,
  useCompanion,
} from "@hooks/use-companion";
import axios from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import CompanionAutocompleteInput from "./CompanionAutocompleteInput";

const MAX_HISTORY_ATTACHMENT_SIZE_MB = 10;

export const CompanionChatInput = ({ status }) => {
  const {
    companionSession,
    setStreamingMessage,
    refetchCompanionSession,
    mostRecentCompanionUsed,
  } = useCompanion();
  const { releaseGlobalCompanionLlmRouter } = useFlags();
  const [sendCompanionSessionMessage, { loading: isSending }] =
    useMutation(SEND_COMPANION_SESSION_MESSAGE);
  const { connectStream } = useStreamResult();
  const { control, resetField } = useForm({
    defaultValues: {
      message: "",
    },
  });

  const message = useWatch({ control, name: "message" });

  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [saveCompanionSessionAttachment] = useMutation(
    UPLOAD_COMPANION_SESSION_ATTACHMENT,
  );

  const currentAttachment = companionSession?.attachments?.[0];

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  }, [companionSession?.companionSessionUuid]);

  if (!companionSession?.companionSessionUuid) {
    return null;
  }

  let inputPlaceholder;
  let sendDisabled;
  if (status === "processing") {
    inputPlaceholder = "Processing Upload...";
    sendDisabled = true;
  } else if (status === "thinking") {
    inputPlaceholder = "Thinking...";
    sendDisabled = true;
  } else {
    inputPlaceholder = releaseGlobalCompanionLlmRouter
      ? "Ask a Companion to help..."
      : "Ask a Companion to help using @[companion name]...";
    sendDisabled = !message.trim() || isSending;
  }

  const onSend = async () => {
    if (sendDisabled) {
      return;
    }

    const lowerMessage = message.toLowerCase();
    const hasExplicitMention = Object.values(
      AvailableCompanions,
    ).some((c) => lowerMessage.includes(`@${c.name}`));

    const isSageMessage = hasExplicitMention
      ? lowerMessage.includes("@sage")
      : !releaseGlobalCompanionLlmRouter &&
        mostRecentCompanionUsed === "SAGE";
    const isQuillMessage = hasExplicitMention
      ? lowerMessage.includes("@quill")
      : !releaseGlobalCompanionLlmRouter &&
        mostRecentCompanionUsed === "QUILL";

    const skipStreaming = isSageMessage || isQuillMessage;

    const response = skipStreaming
      ? { taskUuid: "", error: false } // Skip streaming for sage and quill (quill streams via user WS channel)
      : await connectStream(
          (message) => {
            const companionSessionUuid =
              companionSession.companionSessionUuid;
            setStreamingMessage((prev) => ({
              ...prev,
              [companionSessionUuid]: {
                message:
                  (prev[companionSessionUuid]?.message || "") +
                  message,
                isCompleted: false,
              },
            }));
          },
          () => {
            const companionSessionUuid =
              companionSession.companionSessionUuid;
            setStreamingMessage((prev) => ({
              ...prev,
              [companionSessionUuid]: {
                ...prev[companionSessionUuid],
                isCompleted: true,
              },
            }));
          },
        );

    if (response?.taskUuid === "" && response?.error) {
      alert(
        "error",
        "An error has occurred. Please try again later.",
      );
      return;
    }

    const taskUuid = response?.taskUuid;
    mixpanel.track("Companion Chat Message", {
      message,
      companionSessionUuid: companionSession.companionSessionUuid,
    });
    const pathSegments = location.pathname.split("/");
    const isTemplateEdit =
      pathSegments.includes("templates") &&
      pathSegments.includes("edit");
    const isNoteEdit = pathSegments.includes("edit_note");
    const lastSegment = pathSegments.at(-1);
    const templateUuid = isTemplateEdit ? lastSegment : undefined;
    const noteUuid = isNoteEdit ? lastSegment : undefined;
    await sendCompanionSessionMessage({
      variables: {
        companionSessionUuid: companionSession.companionSessionUuid,
        textContent: message,
        streamingTaskUuid: taskUuid,
        noteUuid,
        templateUuid,
      },
    });
    resetField("message");
    refetchCompanionSession();
  };

  // Handle keyboard navigation for sending messages
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragEnter = () => {
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (isUploading) return;
    setIsDragging(false);
    const { files } = e.dataTransfer;
    onFileSubmit(files);
  };

  const onFilesSelected = (e) => {
    const { files } = e.target;
    onFileSubmit(files);
  };

  const onFileSubmit = async (files) => {
    if (!files.length) return;
    const file = files[0];
    setIsUploading(true);

    let error = null;

    if (files.length > 1) {
      error =
        "Only one file can be uploaded at a time. Multi file support is coming soon.";
    } else if (companionSession?.attachments?.length > 0) {
      error =
        "Only one file can be uploaded at a time. Multi file support is coming soon.";
    } else if (file.type !== "application/pdf") {
      error = "File type not supported. Please upload a PDF file.";
    } else if (
      file.size >
      MAX_HISTORY_ATTACHMENT_SIZE_MB * 1024 * 1024
    ) {
      error = `File is too large (max: ${MAX_HISTORY_ATTACHMENT_SIZE_MB}MB)`;
    }

    if (error) {
      setIsUploading(false);
      alert("error", error);
      return;
    }

    try {
      const signS3Response = await signS3CompanionSessionAttachment(
        "pdf",
        companionSession.companionSessionUuid,
      );
      const { data, storageObjectName } = signS3Response.data;
      const { fields } = data;

      const formData = new FormData();
      for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
      }
      formData.append("file", file);
      formData.append("Content-Type", file.type);
      await axios.post(data.url, formData, {
        headers: {
          "Access-Control-Allow-Origin": true,
        },
      });

      const response = await connectStream(
        (message) => {
          const companionSessionUuid =
            companionSession.companionSessionUuid;
          setStreamingMessage((prev) => ({
            ...prev,
            [companionSessionUuid]: {
              message:
                (prev[companionSessionUuid]?.message || "") + message,
              isCompleted: false,
            },
          }));
        },
        () => {
          const companionSessionUuid =
            companionSession.companionSessionUuid;
          setStreamingMessage((prev) => ({
            ...prev,
            [companionSessionUuid]: {
              ...prev[companionSessionUuid],
              isCompleted: true,
            },
          }));
        },
      );

      if (response?.taskUuid === "" || response?.error) {
        alert(
          "error",
          "An error has occurred. Please try again later.",
        );
        return;
      }

      const taskUuid = response?.taskUuid;
      await saveCompanionSessionAttachment({
        variables: {
          storageObjectName: storageObjectName,
          companionSessionUuid: companionSession.companionSessionUuid,
          fileName: file.name,
          contentType: file.type,
          streamingTaskUuid: taskUuid,
        },
      });
      mixpanel.track("Companion Attachment Uploaded", {
        fileType: file.type,
        fileName: file.name,
        fileSizeMB: file.size / (1024 * 1024),
        companionSessionUuid: companionSession.companionSessionUuid,
      });

      refetchCompanionSession();
    } catch (error) {
      mixpanel.track("Failed to upload file to companion session", {
        error: error.message,
        fileType: file.type,
        fileName: file.name,
        fileSizeMB: file.size / (1024 * 1024),
        companionSessionUuid: companionSession.companionSessionUuid,
      });
      alert("error", "Failed to upload your file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative">
      <div
        className={`flex flex-row items-center rounded-lg border-transparent ${
          isUploading ? "bg-gray-100" : "bg-indigo-50"
        } transition-all duration-200 p-1 border-2 border-dashed data-[dragover=true]:bg-blue-50 data-[dragover=true]:border-blue-400 dark:bg-gray-700/50`}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-dragover={isDragging ? "true" : undefined}
      >
        <label
          htmlFor="companion-file-input"
          className={`transition-colors p-2 flex items-center ${
            isUploading
              ? "text-gray-400 cursor-not-allowed"
              : "text-indigo-500 hover:text-indigo-600 cursor-pointer"
          }`}
          title="Attach file"
        >
          <FontAwesomeIcon
            className="cursor-pointer px-3 "
            icon={faPaperclip}
          />

          <input
            className="hidden"
            id="companion-file-input"
            type="file"
            ref={fileInputRef}
            accept="application/pdf"
            onChange={onFilesSelected}
            disabled={isUploading || currentAttachment}
            data-testid="companion-file-input"
          />
        </label>
        <div className="flex w-full gap-2 rounded-lg">
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <CompanionAutocompleteInput
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                className="w-full p-2 text-sm bg-transparent disabled:text-gray-400 placeholder:text-gray-400 text-gray-700 resize-none max-h-24 overflow-auto dark:text-gray-300 dark:placeholder:text-gray-300"
                autoComplete="off"
              />
            )}
          />

          <button
            onClick={onSend}
            disabled={sendDisabled}
            className={`p-1 transition-colors ${
              sendDisabled
                ? "text-gray-300 cursor-not-allowed"
                : "text-indigo-500 hover:text-indigo-700"
            }`}
            title="Send message"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const CompanionChatInputSection = ({ status }) => {
  const { companionSession } = useCompanion();

  if (!companionSession?.companionSessionUuid) {
    return null;
  }

  return <CompanionChatInput status={status} />;
};
